import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { HttpService } from '@app/core/http/http.service';
import { TenantModel } from '@app/core/models/tenant.model';
import { TenantMenuModel } from '@app/core/models/tenant-menu.model';

@Injectable({ providedIn: 'root' })
export class TenantService {
    private http = inject(HttpService);


    private tenantMenu: TenantMenuModel = null;

    /**
     * Get all tenants
     *
     * @returns {Observable<TenantModel[]>}
     * @member of TenantService
     */
    public getAll(includeDeleted = false): Observable<TenantModel[]> {
        return this.http.get(environment.apiEndpoint + 'tenants?includeDeleted=' + includeDeleted).pipe(
            map(res => (<any>res.body).data),
            catchError((error: any) => throwError(() => error.json().error || 'Server error')));
    }

    /**
     * Get tenant by id
     *
     * @returns {Observable<TenantModel>}
     * @member of TenantService
     */
    public get(tenantId): Observable<TenantModel> {
        if (tenantId) {
            return this.http.get(environment.apiEndpoint + 'tenants/' + tenantId).pipe(
                map(res => (<any>res.body).data),
                catchError((error: any) => throwError(() => error.json().error || 'Server error')));
        }
        return of(null);
    }

    /**
     * Create tenant
     *
     * @param {TenantModel} tenant
     * @returns {Observable<any>}
     * @member of TenantService
     */
    public create(tenant: TenantModel): Observable<any> {
        return this.http.post(environment.apiEndpoint + 'tenants', tenant).pipe(
            catchError((error: any) => throwError(() => error.json().error || 'Server error')));
    }

    /**
     * Update tenant
     *
     * @param {TenantModel} tenant
     * @returns {Observable<any>}
     * @member of TenantService
     */
    public update(tenant: TenantModel): Observable<any> {
        return this.http.put(environment.apiEndpoint + 'tenants', tenant).pipe(
            catchError((error: any) => throwError(() => error.json().error || 'Server error')));
    }

    /**
     * Delete tenant
     * @param {string} tenantId
     * @param deleted
     * @returns {Observable<any>}
     * @member of TenantService
     */
    public delete(tenantId: string, deleted = true): Observable<any> {
        return this.http.delete(environment.apiEndpoint + 'tenants/' + tenantId + '/' + deleted).pipe(
            catchError((error: any) => throwError(() => error.json().error || 'Server error')));
    }

    /**
     * Get menu by Tenant Id
     *
     * @returns {Observable<TenantMenuModel>}
     * @member of TenantService
     */
    public getMenu(tenantId): Observable<TenantMenuModel> {

        if (this.tenantMenu !== null) {
            return of(this.tenantMenu);
        }

        return this.http.get(environment.apiEndpoint + 'tenants/' + tenantId + '/menu')
            .pipe(
                map(res => (<any>res.body).data),
                tap(menus => {
                    this.tenantMenu = menus;
                }),
                catchError((error: any) => throwError(() => error.json().error || 'Server error')));
    }
}
